import { useMemo, useState } from "react";
import { MapPin, Search, ChevronDown, Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CITIES } from "@/cityConfig";

const cityName = (c, lang) => (lang === "tr" ? (c.nameTr || c.nameEn) : (lang === "en" ? c.nameEn : c.name));
const t = (lang, ru, en, tr) => (lang === "tr" ? tr : (lang === "en" ? en : ru));

export function BlogCityPicker({ city, lang, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const sorted = useMemo(() => {
    const locale = lang === "en" ? "en" : (lang === "tr" ? "tr" : "ru");
    return Object.values(CITIES)
      .map((c) => ({ slug: c.slug, label: cityName(c, lang), alt: [c.name, c.nameEn, c.nameTr].filter(Boolean).join(" ") }))
      .sort((a, b) => a.label.localeCompare(b.label, locale));
  }, [lang]);

  const q = query.trim().toLowerCase();
  const filtered = q ? sorted.filter((c) => c.alt.toLowerCase().split(/[\s-]+/).some((w) => w.startsWith(q)) || c.label.toLowerCase().startsWith(q)) : sorted;
  const current = city && CITIES[city] ? cityName(CITIES[city], lang) : null;

  const pick = (slug) => {
    onChange(slug);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group w-full sm:w-80 flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-white border border-stone-200 shadow-sm hover:border-pink-300 hover:shadow-md transition-[border-color,box-shadow] text-left"
          data-testid="blog-city-filter"
        >
          <span className="w-9 h-9 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center shrink-0">
            <MapPin className="w-4 h-4" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[11px] uppercase tracking-[0.14em] text-stone-400">{t(lang, "Город", "City", "Şehir")}</span>
            <span className="block text-sm font-semibold text-stone-900 truncate" data-testid="blog-city-filter-value">
              {current || t(lang, "Все города", "All cities", "Tüm şehirler")}
            </span>
          </span>
          <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[calc(100vw-2rem)] sm:w-80 p-0 rounded-2xl overflow-hidden" data-testid="blog-city-popover">
        <div className="flex items-center gap-2 px-3 border-b border-stone-100">
          <Search className="w-4 h-4 text-stone-400 shrink-0" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(lang, "Найти город…", "Search city…", "Şehir ara…")}
            className="flex-1 py-3 text-base sm:text-sm outline-none bg-transparent"
            data-testid="blog-city-search-input"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="p-1 text-stone-400 hover:text-stone-700" data-testid="blog-city-search-clear">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <ul className="max-h-72 overflow-y-auto py-1" data-testid="blog-city-list">
          {!q && (
            <CityRow label={t(lang, "Все города", "All cities", "Tüm şehirler")} active={!city} onClick={() => pick("")} testId="blog-city-option-all" />
          )}
          {filtered.map((c) => (
            <CityRow key={c.slug} label={c.label} active={city === c.slug} onClick={() => pick(c.slug)} testId={`blog-city-option-${c.slug}`} />
          ))}
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-stone-400" data-testid="blog-city-no-results">
              {t(lang, "Город не найден", "No city found", "Şehir bulunamadı")}
            </li>
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function CityRow({ label, active, onClick, testId }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors ${active ? "bg-pink-50 text-pink-700 font-semibold" : "text-stone-700 hover:bg-stone-50"}`}
        ref={active ? (el) => { if (el) { const ul = el.closest("ul"); ul.scrollTop = el.offsetTop - ul.clientHeight / 2; } } : undefined}
        data-testid={testId}
      >
        {label}
        {active && <Check className="w-4 h-4" />}
      </button>
    </li>
  );
}
